import { useTranslation } from 'react-i18next';
import { useNoticeStackReserve } from '@/hooks/useNoticeStackReserve';
import type { SuggestedVerse } from '@/lib/verse-suggest';
import { ScriptureVerseList } from './ScriptureVerseList';
import styles from './ScriptureVersesSheet.module.css';

interface ScriptureVersesSheetProps {
  /** The prayer's saved verses, already resolved from the local bundle
   *  by the caller's saved-only resolver (resolveSavedVerses). */
  verses: SuggestedVerse[];
  onClose: () => void;
  /** Coordinator-computed z for the layer (rule #78). PrayerDetail uses
   *  the `scripture-verses` key; Garden uses its own `garden-scripture`. */
  zIndex?: number;
  /** i18n key for the close-button aria-label. Defaults to
   *  `scripture.closeAria` ("Back to your prayer", correct from
   *  PrayerDetail). Garden passes a neutral key (`nav.back`) because
   *  closing there returns to the Garden, not a prayer. */
  closeAriaKey?: string;
}

/**
 * Read-only "Scripture for this prayer" sheet (rule #80, the verse lane).
 * Opened from PrayerDetail's scripture line; shows the verses a SHARE
 * already saved onto the prayer (prayer.verseResult), resolved from the
 * local bundle. It NEVER searches: no matching API, no paid endpoint, no
 * rawText to Grok, no Supabase write (the caller's resolver is pure-read;
 * the share flow self-heals any bundle drift on its next open). Private
 * prayers DO show their saved verses here — privacy is the never-SHARE
 * gate, not an in-app-view gate (rule #27). Mounted from TWO surfaces,
 * each with its own NavStack layer (rule #78), each mounting it closed ->
 * StrictMode-safe: PrayerDetail (`scripture-verses` key, default
 * `closeAriaKey`) and the Garden vine card's verse action
 * (`garden-scripture` key, neutral `closeAriaKey="nav.back"`). The caller
 * resolves the verses (PrayerDetail's `resolveSavedVerses`; Garden's View
 * resolves the same, Find resolves via `getVersesForPrayer`) and passes
 * the pre-resolved set in. Full-screen overlay in the PrayerDetail family.
 */
export default function ScriptureVersesSheet({
  verses,
  onClose,
  zIndex,
  closeAriaKey = 'scripture.closeAria',
}: ScriptureVersesSheetProps) {
  const { t } = useTranslation();
  // App notice stack (v0.2.0 finishing): an in-flow spacer at the top of the
  // body keeps the title and the verses below the fixed notices (0 while no
  // notice shows; the fixed Back button sits above the stack). Chrome only.
  const [noticeReserve, noticeReserveRef] = useNoticeStackReserve();
  return (
    <div
      className={styles.root}
      style={zIndex !== undefined ? { zIndex } : undefined}
      role="dialog"
      aria-modal="true"
      aria-label={t('scripture.sheetTitle')}
    >
      <button
        type="button"
        className={styles.back}
        onClick={onClose}
        aria-label={t(closeAriaKey)}
      >
        <svg
          width="30"
          height="30"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <polyline points="14 6 8 12 14 18" />
        </svg>
      </button>

      <div className={styles.body}>
        <div
          ref={noticeReserveRef}
          className={styles.noticeReserve}
          style={{ height: noticeReserve }}
          data-notice-reserve={noticeReserve}
          aria-hidden="true"
        />
        <h2 className={styles.title}>{t('scripture.sheetTitle')}</h2>
        <ScriptureVerseList verses={verses} />
      </div>
    </div>
  );
}
